/**
 * @fileoverview
 *
 * @author Nathan Buchar
 */

import $ from 'jquery';
import SparkCarousel from 'spark-carousel';
import SparkWindowEvents from 'spark-window-events';
import { array, style } from 'spark-helpers';
import './blockquote-carousel.scss';

class BlockquoteCarousel {
  constructor(element) {
    /**
     * The base module element.
     *
     * @type {HTMLElement}
     */
    this.element = element;

    /**
     * Local reference to the carousel base element.
     *
     * @type {HTMLElement}
     * @default null
     * @private
     */
    this._carouselElement = null;

    /**
     * Local reference to the carousel slide elements.
     *
     * @type {NodeList}
     * @default null
     * @private
     */
    this._carouselSlideElementsArray = null;

    /**
     * Local reference to the carousel slide elements as an array.
     *
     * @type {Array}
     * @default null
     * @private
     */
    this._carouselSlideElementsArray = null;

    /**
     * Reference to the Spark Carousel instance.
     *
     * @type {SparkCarousel}
     * @default null
     * @private
     */
    this._carousel = null;

    this._init();
  }

  /**
   * Initialize this module.
   *
   * @private
   */
  _init() {
    this._initElements();
    this._initSparkCarousel();
    this._initSlideHeights();
  }

  /**
   * Creates local references to elements.
   *
   * @private
   */
  _initElements() {
    this._carouselElement =
      this.element.querySelector(`.${SparkCarousel.settings.Classes.BASE}`);

    this._carouselSlideElements =
      this.element.querySelectorAll(BlockquoteCarousel.Selectors.SLIDE);

    this._carouselSlideElementsArray = array.toArray(this._carouselSlideElements);
  }

  /**
   * Initializes the SparkCarousel instance.
   *
   * @private
   */
  _initSparkCarousel() {
    this._carousel = new SparkCarousel(this._carouselElement, {
      isFade: true,
      isSlideshow: true,
      isLooped: true,
      slideshowSpeed: 7000,
      pagination: true,
      template: {
        paddleNextInner: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17.021 29.798" enable-background="new 0 0 17.021 29.798"><path d="M2.121 0l-2.121 2.121 12.778 12.778-12.778 12.778 2.121 2.121 14.9-14.899z"/></svg>',
        paddlePrevInner: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17.021 29.798" enable-background="new 0 0 17.021 29.798"><path d="M14.899 29.798l2.122-2.121-12.778-12.778 12.778-12.778-2.122-2.121-14.899 14.899z"/></svg>',
      },
    });

    this._carousel.startSlideshow();
  }

  /**
   * Ensures that the height of the slides are the height of the largest slide.
   *
   * @private
   */
  _initSlideHeights() {
    this._updateSlideHeights();

    SparkWindowEvents.onResize(() => {
      this._updateSlideHeights();
    });
  }

  /**
   * Ensures that the height of the slides are the height of the largest slide.
   *
   * @private
   */
  _updateSlideHeights() {
    style.evenHeights(this._carouselSlideElementsArray);
  }

  dispose() {
    this.element = null;
  }

  static initializeAll() {
    return $('.blockquote-carousel').map((i, el) => new BlockquoteCarousel(el)).get();
  }
}

BlockquoteCarousel.ClassNames = {
  BASE: 'blockquote-carousel',
  SLIDE: 'blockquote-carousel__slide',
};

BlockquoteCarousel.Selectors = {
  BASE: `.${BlockquoteCarousel.ClassNames.BASE}`,
  SLIDE: `.${BlockquoteCarousel.ClassNames.SLIDE}`,
};

export default BlockquoteCarousel;
